import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box } from '@mui/material';
import React, { useEffect, useCallback } from 'react';

declare interface Props {
  onClose: (ok: boolean) => void;
  open: boolean;
  cancelText?: string;
  cancelHidden?: boolean;
  okText?: string;
  okDisabled?: boolean;
  title: string;
  maxWidth?: false | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  keyCode?: string;
  children?: React.ReactNode;
  titleIconStart?: React.ReactNode;
  okButtonId?: string;
  titleOnly?: boolean;
}
const ConfirmationDialog: React.FC<React.PropsWithChildren<Props>> = ({
  onClose,
  open,
  children,
  cancelText,
  okText,
  title,
  maxWidth,
  okDisabled,
  cancelHidden,
  keyCode,
  titleIconStart,
  okButtonId,
  titleOnly,
}) => {
  const onKeyDown = (event: React.KeyboardEvent<HTMLElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      handleOk();
    }
  };

  const handleCancel = useCallback(() => {
    onClose(false);
  }, [onClose]);

  const handleOk = useCallback(() => {
    onClose(true);
  }, [onClose]);

  useEffect(() => {
    if (keyCode == 'Enter') {
      handleOk();
    }
    if (keyCode == 'Escape') {
      handleCancel();
    }
  }, [keyCode, handleOk, handleCancel]);

  return (
    <Dialog maxWidth={maxWidth || 'xs'} fullWidth aria-labelledby="confirmation-dialog-title" open={open}>
      <DialogTitle id="confirmation-dialog-title">
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {titleIconStart && <Box sx={{ display: 'flex', alignItems: 'center', pr: 1 }}>{titleIconStart}</Box>}
          <Box>{title}</Box>
        </Box>
      </DialogTitle>
      {!titleOnly && <DialogContent dividers>{children}</DialogContent>}
      <DialogActions>
        {!cancelHidden || null ? (
          <Button autoFocus onClick={handleCancel} color="primary" variant="outlined">
            {cancelText || 'Cancel'}
          </Button>
        ) : null}
        <Button onClick={handleOk} id={okButtonId} onKeyDown={(e) => onKeyDown(e)} color="primary" disabled={okDisabled} variant="outlined">
          {okText || 'OK'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog;
